import { Selection, TextSelection } from "@tiptap/pm/state";
import { makeAutoObservable, computed } from "mobx";

import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { getCaretPositionFromCoordinates } from "@/input/utils/getCaretPositionFromCoordinates";
import { findSelectionCell } from "@/models/cursor/jumpLine";
import { CURSOR_POSITION_CLASS_NAME } from "@/models/cursor/moveUpDown";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { Editor } from "@tiptap/core";

export type InlineNodeRange = {
  readonly node: InlineNodeModel;
  readonly anchor: number;
  readonly head: number;
  readonly from: number;
  readonly to: number;
  readonly key: string;
};

export type InlineNodeRanges = Map<string, InlineNodeRange>;

export type SelectionDump = Record<string, InlineNodeRange>;

/**
 * Find the closest cursor index within an inline node at a given X coordinate.
 * Queries DOM cursor-position elements and returns the index of the closest one.
 */
function findCursorIndexAtX(
  tiptapDom: HTMLElement,
  inlineNodeId: string,
  clientX: number,
): number | undefined {
  const cursors = tiptapDom.querySelectorAll<HTMLElement>(
    `.${CURSOR_POSITION_CLASS_NAME}[data-node-id="${inlineNodeId}"]`,
  );

  let closestIndex: number | undefined;
  let closestDist = Infinity;

  for (const cursor of cursors) {
    const { cursorIndex, cursorPosition } = cursor.dataset;
    if (cursorIndex === undefined) continue;

    const rect = cursor.getBoundingClientRect();
    const cursorLeft = cursorPosition === "end" ? rect.right : rect.left;
    const dist = Math.abs(clientX - cursorLeft);

    if (dist < closestDist) {
      closestDist = dist;
      closestIndex = parseInt(cursorIndex, 10);
    }
  }

  return closestIndex;
}

export class SelectionModel {
  doc: DocumentModel;

  inlineNodeRanges: InlineNodeRanges = new Map();

  private _startSelectionPosition: {
    node: InlineNodeModel;
    index: number;
    anchorPos?: number;
  } | null = null;

  get startSelectionPosition() {
    return this._startSelectionPosition;
  }

  private set startSelectionPosition(
    value: { node: InlineNodeModel; index: number; anchorPos?: number } | null,
  ) {
    this._startSelectionPosition = value;
  }

  isPointerSelecting: boolean = false;

  isKeyboardSelecting: boolean = false;

  /** Current mouse X during pointer drag (tracked for partial expression selection) */
  pointerClientX: number | undefined = undefined;

  /** Range keys set by the mousemove handler for adjacent expressions (protected from onSelectionUpdate cleanup) */
  pointerDragRangeKeys: Set<string> = new Set();

  /** ProseMirror anchor position captured when a math-initiated drag starts */
  private pointerDragAnchorPos: number | undefined = undefined;

  /**
   * State for a single-mousedown drag that manually drives the editing node's input
   * text selection (see startEditingNodePointerSelect).
   */
  private editingNodeSelect: { anchor: number | null } | null = null;

  constructor(doc: DocumentModel) {
    this.doc = doc;

    makeAutoObservable(this, {
      allSelectedNodes: computed.struct,
      isMathOnlySelection: false,
    });
  }

  dispose() {
    this.resetSelection();
  }

  getSelectionRange(): InlineNodeRanges {
    return this.inlineNodeRanges;
  }

  dumpSelection(): SelectionDump {
    return [...this.inlineNodeRanges.entries()].reduce(
      (result, [key, val]) => {
        const value = { ...val };
        result[key] = value;
        return result;
      },
      {} as Record<string, InlineNodeRange>,
    );
  }

  restoreSelection(selectionDump: SelectionDump) {
    this.resetInlineNodeRanges();
    Object.keys(selectionDump).forEach((key) => {
      this.inlineNodeRanges.set(key, selectionDump[key]);
    });
  }

  get direction(): "forward" | "backward" | undefined {
    if (this.inlineNodeRanges.size > 1) {
      return undefined;
    }
    const { anchor, head } = this.lastUpdatedInlineNodeRange || {};
    if (anchor !== undefined && head !== undefined) {
      return anchor < head ? "forward" : "backward";
    }
    return undefined;
  }

  get input() {
    return this.doc.input;
  }

  private startSelection() {
    const { node, index } = this.doc.cursor;
    if (!node || index === undefined) {
      return;
    }
    const anchorPos = this.doc.editor.tiptap?.state.selection.anchor;
    this.startSelectionPosition = { node, index, anchorPos };
  }

  startPointerSelection() {
    const { tiptap } = this.doc.editor;
    if (!tiptap) {
      return;
    }

    this.startSelection();
    this.pointerDragAnchorPos = tiptap.state.selection.anchor;
  }

  /**
   * Arm a manual pointer-driven text selection inside an inline-editable node that is
   * being entered by a single mousedown.
   */
  startEditingNodePointerSelect() {
    this.editingNodeSelect = { anchor: null };
  }

  /**
   * Unified mousemove handler for both math-initiated and text-initiated drags.
   * Called from the capture-phase listener in the ExpressionSelection Plugin.
   */
  handlePointerDragMove(e: MouseEvent): void {
    if (!e.clientX && !e.clientY) return;

    const { editingNode } = this.doc.input;

    if (editingNode?.isInlineEditable()) {
      return this.handleEditingNodePointerDragMove(editingNode, e.clientX);
    }

    const { tiptap } = this.doc.editor;
    if (!tiptap) return;

    this.pointerClientX = e.clientX;
    this.pointerDragRangeKeys.clear();

    if (this.startSelectionPosition && this.pointerDragAnchorPos !== undefined) {
      // Math-initiated drag: manage ProseMirror text selection and partial expression selection
      this.isPointerSelecting = true;

      const anchorPos = this.pointerDragAnchorPos;
      let headPos = tiptap.view.posAtCoords({ left: e.clientX, top: e.clientY })?.pos;
      if (headPos && headPos !== anchorPos && headPos !== anchorPos + 1) {
        const isForward = headPos > anchorPos;

        // Include adjacent expression when mouse is over it but posAtCoords
        // hasn't crossed the midpoint yet
        const adjacentPos = isForward ? headPos : headPos - 1;
        if (adjacentPos >= 0) {
          const adjacentNode = tiptap.state.doc.nodeAt(adjacentPos);
          if (adjacentNode && nodeIsExpression(adjacentNode)) {
            headPos = isForward ? adjacentPos + 1 : adjacentPos;
          }
        }

        tiptap
          .chain()
          .focus()
          .setTextSelection({ from: isForward ? anchorPos : anchorPos + 1, to: headPos })
          .run();

        this.updatePartialPointerSelection(e.clientX, tiptap, isForward);
      }
    } else if (this.pointerDragAnchorPos === undefined && !this.startSelectionPosition) {
      // Text-initiated drag: find expression under mouse and set partial selection.
      // Guard: In that transient window between the merge microtask
      // and mouseup, entering this branch would incorrectly set a partial selection
      // from 0 using the NodeSelection left on the merged expression.
      // handlePointerDragEnd unconditionally clears pointerDragAnchorPos on mouseup,
      // so subsequent text drags are unaffected.
      const { from, to } = tiptap.state.selection;
      if (from !== to) {
        const { anchor, head } = tiptap.state.selection;
        const isForward = head > anchor;
        const direction = isForward ? "forward" : "backward";

        // Use posAtCoords to find the expression under the mouse.
        // Check both pos and pos-1 because posAtCoords returns position
        // before/after atom based on midpoint.
        const posInfo = tiptap.view.posAtCoords({ left: e.clientX, top: e.clientY });
        if (posInfo) {
          const wrapperNode = [posInfo.pos, posInfo.pos - 1]
            .filter((p) => p >= 0 && p < tiptap.state.doc.content.size)
            .map((p) => {
              const node = tiptap.state.doc.nodeAt(p);
              const id = node && nodeIsExpression(node) ? String(node.attrs.nodeId ?? "") : "";
              return id ? this.doc.nodes.get(id) : undefined;
            })
            .find((n): n is WrapperNodeModel => !!n?.is(WrapperNodeModel));

          if (wrapperNode) {
            const range = this.setPartialSelectionAtPointer(wrapperNode, direction);
            if (range) {
              this.pointerDragRangeKeys.add(range.key);
            }
          }
        }
      }
    }
  }

  /**
   * Handles pointer drag movement when an editing node is selected.
   * @param node The node being edited.
   */
  private handleEditingNodePointerDragMove(node: NodeModel, clientX: number) {
    if (this.editingNodeSelect) {
      const input = this.doc.input.inputElement;
      const { initCaretPosition } = this.doc.input;

      if (!input || !initCaretPosition || !("left" in initCaretPosition)) {
        return;
      }

      const { left } = input.getBoundingClientRect();

      if (this.editingNodeSelect.anchor === null) {
        this.editingNodeSelect.anchor = getCaretPositionFromCoordinates(
          input,
          initCaretPosition.left - left,
        );
      }

      const { anchor } = this.editingNodeSelect;
      const head = getCaretPositionFromCoordinates(input, clientX - left);

      input.setSelectionRange(
        Math.min(anchor, head),
        Math.max(anchor, head),
        head >= anchor ? "forward" : "backward",
      );
    }

    const { inputSelection } = this.doc.input;
    const { value } = this.doc.input;

    if (!inputSelection) {
      return;
    }

    const atLeftEdge = inputSelection.from === 0 && inputSelection.to > 0;
    const atRightEdge = inputSelection.from < value.length && inputSelection.to === value.length;

    if (!atLeftEdge && !atRightEdge) return;

    this.editingNodeSelect = null;
    this.doc.input.reset(true);

    if (atLeftEdge) {
      this.doc.cursor.moveAfterNode(node);
    } else {
      this.doc.cursor.moveBeforeNode(node);
    }

    this.selectNode(node);
    this.startPointerSelection();
  }

  /**
   * Unified mouseup handler for both math-initiated and text-initiated drags.
   * Called from the ExpressionSelection Plugin.
   */
  handlePointerDragEnd(): void {
    if (this.isPointerSelecting && this.startSelectionPosition) {
      this.startSelectionPosition = null;
    }
    this.isPointerSelecting = false;
    this.pointerDragAnchorPos = undefined;
    this.pointerClientX = undefined;
    this.pointerDragRangeKeys.clear();
    this.editingNodeSelect = null;

    this.coverPartiallySelectedBoundaryExpressions();
  }

  /**
   * After a mouse selection settles, widen the ProseMirror selection to cover a boundary
   * math atom whose content is only partially selected. The partial range lives in
   * `inlineNodeRanges`, but a mouse drag/shift+click leaves the atom itself just outside
   * the PM selection — unlike a keyboard selection, which covers it. Aligning them means
   * the same visible selection has the same representation, so consumers (copy, cut, …)
   * behave identically regardless of how it was made.
   */
  private coverPartiallySelectedBoundaryExpressions(): void {
    const { tiptap } = this.doc.editor;
    if (!tiptap) return;

    const { from, to, anchor, head } = tiptap.state.selection;
    if (from === to) return;

    const newFrom = this.isPartiallySelectedExpressionAt(from - 1) ? from - 1 : from;
    const newTo = this.isPartiallySelectedExpressionAt(to) ? to + 1 : to;
    if (newFrom === from && newTo === to) return;

    // Preserve the selection direction (anchor/head order).
    const forward = head >= anchor;
    tiptap.commands.command(({ tr, dispatch }) => {
      if (dispatch) {
        tr.setSelection(
          TextSelection.create(tr.doc, forward ? newFrom : newTo, forward ? newTo : newFrom),
        );
      }
      return true;
    });
  }

  private isPartiallySelectedExpressionAt(pos: number): boolean {
    const { tiptap } = this.doc.editor;
    if (!tiptap || pos < 0 || pos >= tiptap.state.doc.content.size) return false;

    const node = tiptap.state.doc.nodeAt(pos);
    if (!node || !nodeIsExpression(node)) return false;

    const wrapper = this.doc.nodes.get(String(node.attrs.nodeId ?? ""));
    return (
      !!wrapper &&
      wrapper.is(WrapperNodeModel) &&
      this.getSelectedNodesInWrapper(wrapper).length > 0
    );
  }

  updatePointerSelection(node: InlineNodeModel, index: number) {
    if (!this.isPointerSelecting || !this.startSelectionPosition) {
      return false;
    }

    const { node: fromNode, index: fromIndex } = this.startSelectionPosition;

    const nodeParentWrapperNode = node.ancestors.find((n) => n.is(WrapperNodeModel));
    const fromNodeParentWrapperNode = fromNode.ancestors.find((n) => n.is(WrapperNodeModel));

    // Prevent update selectionRange outside of current focused wrapper node
    if (nodeParentWrapperNode?.id !== fromNodeParentWrapperNode?.id) {
      return false;
    }
    this.resetInlineNodeRanges();

    return this.setInlineNodeRangeBetween(fromNode, fromIndex, node, index);
  }

  /**
   * Set partial selection within boundary expressions during mouse drag.
   * For the head expression (where mouse is): select from edge to cursor position at clientX.
   * For the anchor expression (where drag started): select from start position to edge.
   */
  private updatePartialPointerSelection(clientX: number, tiptap: Editor, isForward: boolean): void {
    const { from, to } = tiptap.state.selection;

    // Handle HEAD expression (where mouse currently is)
    const headExprPos = isForward ? to - 1 : from;
    const headNode = tiptap.state.doc.nodeAt(headExprPos);

    let headWrapperId: string | undefined;

    if (headNode && nodeIsExpression(headNode)) {
      const wrapperNodeId = String(headNode.attrs.nodeId ?? "");
      const wrapperNode = this.doc.nodes.get(wrapperNodeId);

      if (wrapperNode?.is(WrapperNodeModel) && wrapperNode.inlineNode) {
        headWrapperId = wrapperNode.id;
        const { inlineNode } = wrapperNode;
        const cursorIndex = findCursorIndexAtX(tiptap.view.dom, inlineNode.id, clientX);

        if (cursorIndex !== undefined) {
          if (isForward) {
            this.setInlineNodeRangeBetween(inlineNode, 0, inlineNode, cursorIndex);
          } else {
            this.setInlineNodeRangeBetween(
              inlineNode,
              inlineNode.nodes.length,
              inlineNode,
              cursorIndex,
            );
          }
        }
      }
    }

    // Handle ANCHOR expression (where selection started from)
    if (this.startSelectionPosition) {
      const { node: startNode, index: startIndex } = this.startSelectionPosition;
      const startWrapper = startNode.ancestors.find((n) => n.is(WrapperNodeModel));

      if (startWrapper?.inlineNode && startWrapper.id !== headWrapperId) {
        const anchorInline = startWrapper.inlineNode;
        if (isForward) {
          this.setInlineNodeRangeBetween(
            startNode,
            startIndex,
            anchorInline,
            anchorInline.nodes.length,
          );
        } else {
          this.setInlineNodeRangeBetween(anchorInline, 0, startNode, startIndex);
        }
      }
    }
  }

  /**
   * Set partial selection for a boundary expression using the tracked pointer X coordinate.
   * Used by onSelectionUpdate for text-initiated drags where the mouse enters a math expression.
   */
  setPartialSelectionAtPointer(
    wrapperNode: WrapperNodeModel,
    direction: "forward" | "backward",
  ): InlineNodeRange | false {
    if (this.pointerClientX === undefined) return false;
    const { inlineNode } = wrapperNode;
    if (!inlineNode) return false;
    const tiptapDom = this.doc.editor.tiptap?.view.dom;
    if (!tiptapDom) return false;

    const cursorIndex = findCursorIndexAtX(tiptapDom, inlineNode.id, this.pointerClientX);
    if (cursorIndex === undefined) return false;

    const anchor = direction === "forward" ? 0 : inlineNode.nodes.length;
    if (anchor === cursorIndex) return false;

    return this.setInlineNodeRange(inlineNode, anchor, cursorIndex) || false;
  }

  resetSelection(resetMatrix = true): void {
    if (this.isKeyboardSelecting) {
      return;
    }

    if (this.inlineNodeRanges.size > 0 && !this.doc.input.editingNode) {
      this.doc.editor.suggestions.reset();
    }

    this.startSelectionPosition = null;
    this.resetInlineNodeRanges();

    if (resetMatrix) {
      this.doc.matrixSelection.resetSelection();
    }
  }

  /**
   * Get selected nodes from the last updated inlineNodeRange
   * TODO: rename to continuousSelectedNodes https://linear.app/corca-research/issue/D-1946/refactor-selectednodesallselectednodescontinuousselectednodes
   */
  get selectedNodes(): NodeModel[] {
    if (this.inlineNodeRanges.size > 1) {
      return [];
    }
    const range = [...this.inlineNodeRanges.values()][0];
    if (range) {
      const { node, from, to } = range;
      return node.nodes.slice(from, to);
    }
    return [];
  }

  /**
   * Get selected nodes inside a node. If this is not an inline node, it will find first selection
   * inside this node.
   */
  getSelectedNodes(parentNode: InlineNodeModel | NodeModel): NodeModel[] {
    let inlineNode: InlineNodeModel | undefined;
    if (parentNode.is(InlineNodeModel)) {
      inlineNode = parentNode;
    } else {
      inlineNode = Array.from(this.inlineNodeRanges.values()).find((range) =>
        range.node.ancestors.includes(parentNode),
      )?.node;
    }

    if (inlineNode) {
      const range = this.inlineNodeRanges.get(inlineNode.id);
      if (range) {
        const { node, from, to } = range;
        return node.nodes.slice(from, to);
      }
    }
    return [];
  }

  /**
   * Get selected nodes inside a wrapper node. It will find the first inline node with selection
   * inside the wrapper and return this seletion.
   */
  getSelectedNodesInWrapper(parentNode: WrapperNodeModel): NodeModel[] {
    const inlineNode = Array.from(this.inlineNodeRanges.values()).find((range) =>
      range.node.ancestors.includes(parentNode),
    )?.node;
    if (inlineNode) {
      return this.getSelectedNodes(inlineNode);
    }
    return [];
  }

  get lastUpdatedInlineNodeRange(): InlineNodeRange | undefined {
    return [...this.inlineNodeRanges.values()].at(-1);
  }

  /**
   * Follow the current cursor and select everything between the start and end of the cursor movement.
   * The actions for movement are passed as the first parameter of the function.
   *
   * For example:
   *
   * ```
   * doc.selection.selectAfterCursor(() => {
   *   doc.cursor.moveToPrevSibling();
   * });
   * ```
   */
  selectAfterCursor(actions: () => void, disabled = false): void {
    if (disabled) {
      actions();
      return;
    }

    const { tiptap } = this.doc.editor;

    if (!this.startSelectionPosition) {
      this.startSelection();
    }

    const { node: oldNode, index: oldIndex } = this.startSelectionPosition || {};
    if (!oldNode || oldIndex === undefined) {
      return;
    }

    const prevPos = tiptap?.state.selection.anchor ?? -1;

    this.isKeyboardSelecting = true;
    actions();
    this.isKeyboardSelecting = false;

    const pos = tiptap?.state.selection.anchor ?? -1;

    if (tiptap) {
      const oldCell = findSelectionCell(prevPos, tiptap);
      const newCell = findSelectionCell(pos, tiptap);

      if (oldCell && newCell && oldCell.node !== newCell.node) {
        tiptap
          .chain()
          .setCellSelection({ anchorCell: oldCell.pos, headCell: newCell.pos })
          .focus()
          .run();
        return;
      }
    }

    const { node: newNode, index: newIndex } = this.doc.cursor;
    if (newNode && newIndex !== undefined) {
      this.resetInlineNodeRanges();
      const rangeResult = this.setInlineNodeRangeBetween(oldNode, oldIndex, newNode, newIndex);

      // Handle cross-expression selection (nodes in different expression trees)
      if (!rangeResult && this.doc.cursor.isActive && tiptap) {
        if (this.selectCrossExpression(oldNode, oldIndex, newNode, newIndex)) {
          return;
        }
      }
    }

    if (!this.doc.cursor.isActive && prevPos !== -1 && pos !== -1) {
      const direction = pos > prevPos ? "forward" : "backward";

      const nodeParentInlineNode = oldNode.ancestors.find((n) =>
        n.is(WrapperNodeModel),
      )?.inlineNode;
      // The exited atom is "left behind" (not part of the selection) when its range is
      // empty — e.g. exiting rightward from the end of the expression.
      let leftBehind = false;
      if (nodeParentInlineNode) {
        const exitedAtomRange = this.setInlineNodeRangeBetween(
          oldNode,
          oldIndex,
          nodeParentInlineNode,
          direction === "forward" ? nodeParentInlineNode.nodes.length : 0,
        );
        leftBehind = !exitedAtomRange || exitedAtomRange.anchor === exitedAtomRange.head;
      }

      // Extend head one step past the expression boundary into text so the cursor is visible immediately
      let textHead = pos;
      if (tiptap) {
        const step = direction === "backward" ? -1 : 1;
        const clamped = Math.max(0, Math.min(pos + step, tiptap.state.doc.content.size));
        const sel = Selection.findFrom(tiptap.state.doc.resolve(clamped), step, true);
        if (sel) textHead = sel.head;
      }

      // The atom occupies [prevPos, prevPos + 1]. When it's left behind, anchor on the
      // edge the cursor left from (so re-crossing the expression re-includes it); when it
      // stays selected, anchor on the opposite edge so the tiptap range keeps covering it.
      const anchorAfterAtom = direction === "forward" ? leftBehind : !leftBehind;
      const from = anchorAfterAtom ? prevPos + 1 : prevPos;
      tiptap?.chain().setTextSelection({ from, to: textHead }).focus().run();

      return;
    }
  }

  get hasSelectedEditableNode(): boolean {
    return this.selectedNodes.length === 1 && this.selectedNodes[0]?.isEditable;
  }

  editSelectedNode(): void {
    if (this.hasSelectedEditableNode) {
      this.doc.input.editNode(this.selectedNodes[0]);
      this.resetSelection();
    }
  }

  get hasSelectedNodes(): boolean {
    return this.inlineNodeRanges.size > 0;
  }

  get hasTextSelection(): boolean {
    const tiptapSelection = this.doc.editor.tiptap?.state.selection;
    return !this.doc.cursor.isActive && !!tiptapSelection && !tiptapSelection.empty;
  }

  get hasActionableSelection(): boolean {
    return this.hasSelectedNodes || this.hasTextSelection;
  }

  get isContinuousSelection(): boolean {
    return this.inlineNodeRanges.size === 1;
  }

  get isMathOnlySelection(): boolean {
    const { tiptap } = this.doc.editor;
    if (!tiptap) return false;

    return tiptap.state.selection.content().size === 1 && this.hasSelectedNodes;
  }

  get from(): number | undefined {
    return this.range?.from;
  }

  get to(): number | undefined {
    return this.range?.to;
  }

  get range(): InlineNodeRange | undefined {
    if (this.inlineNodeRanges.size > 1) {
      return undefined;
    }
    return this.lastUpdatedInlineNodeRange;
  }

  isSelected(node: NodeModel) {
    return computed(() => {
      return this.isInlineNodeRangeSelected(node);
    }).get();
  }

  expandRangeToNode(node: NodeModel) {
    const toNode = node.parent;
    if (toNode?.is(InlineNodeModel)) {
      this.expandRangeToInlinePosition(toNode, node.index, true);
    }
  }

  /**
   * Extends selection from the current anchor to the cursor position
   * (`toNode`, `toIndex`). Use `inclusive` when `toIndex` is a node index so the node itself is included.
   *
   * Use `inclusive: false` when `toIndex` is already a cursor position.
   */
  expandRangeToInlinePosition(toNode: InlineNodeModel, toIndex: number, inclusive = false) {
    // Capture the anchor on the first shift+click so subsequent shift+clicks
    // expand or shrink relative to the same anchor (matches shift+arrow).
    if (!this.startSelectionPosition) {
      this.startSelection();
    }

    const startPos = this.startSelectionPosition;
    const fromNode = startPos?.node ?? this.doc.cursor.node;
    const fromIndex = startPos?.index ?? this.doc.cursor.index;

    if (fromNode && fromIndex !== undefined) {
      this.resetInlineNodeRanges();
      const rangeResult = this.setInlineNodeRangeBetween(fromNode, fromIndex, toNode, toIndex, {
        inclusive,
      });

      if (rangeResult) {
        // Same-wrapper case. Move the math cursor to the click position so
        // setTextCursor re-anchors tiptap.selection on this wrapper (otherwise
        // the stale tiptap selection from a previous cross-wrapper click stays
        // and the area beyond the wrapper keeps the native text highlight).
        this.doc.cursor.moveTo(toNode, toIndex, { resetSelection: false });
      } else {
        this.selectCrossExpression(fromNode, fromIndex, toNode, toIndex, {
          inclusiveTarget: inclusive,
        });
      }
    } else {
      this.extendSelectionFromTextToMath(toNode, toIndex, inclusive);
    }
  }

  extendSelectionFromMathCursor(clickPos: number): boolean {
    const { tiptap } = this.doc.editor;

    if (!tiptap) return false;

    if (!this.startSelectionPosition) {
      this.startSelection();
    }

    let anchor = this.startSelectionPosition;

    if (!anchor) {
      anchor =
        this.doc.cursor.node && this.doc.cursor.index !== undefined
          ? { node: this.doc.cursor.node, index: this.doc.cursor.index }
          : null;
    }

    if (!anchor) return false;

    const wrapper = anchor.node.ancestors.find((n): n is WrapperNodeModel =>
      n.is(WrapperNodeModel),
    );
    if (!wrapper) return false;

    let wrapperPos: number | undefined;

    tiptap.state.doc.descendants((n, pos) => {
      if (n.attrs?.nodeId === wrapper.id) {
        wrapperPos = pos;
        return false;
      }
      return true;
    });

    if (wrapperPos === undefined || (clickPos >= wrapperPos && clickPos <= wrapperPos + 1)) {
      return false;
    }

    const isForward = clickPos > wrapperPos;
    this.startSelectionPosition = { node: anchor.node, index: anchor.index };

    this.doc.cursor.reset();
    this.doc.input.reset(true);

    this.extendCrossExpressionSourceRange(isForward ? "forward" : "backward");

    const newAnchor = isForward ? wrapperPos : wrapperPos + 1;
    tiptap.chain().setTextSelection({ from: newAnchor, to: clickPos }).focus().run();

    return true;
  }

  private extendSelectionFromTextToMath(
    toNode: InlineNodeModel,
    toIndex: number,
    inclusive: boolean,
  ) {
    const { tiptap } = this.doc.editor;
    if (!tiptap) return;

    const toWrapper = toNode.ancestors.find((n): n is WrapperNodeModel => n.is(WrapperNodeModel));
    if (!toWrapper) return;

    let toExprPos: number | undefined;
    tiptap.state.doc.descendants((n, pos) => {
      if (n.attrs?.nodeId === toWrapper.id) {
        toExprPos = pos;
        return false;
      }
      return true;
    });
    if (toExprPos === undefined) return;

    const anchorPos = tiptap.state.selection.anchor;
    const isForward = anchorPos < toExprPos;
    const topInline = toWrapper.inlineNode;

    if (topInline) {
      if (isForward) {
        const cutIndex = this.getIndexInParentInline(toNode, toIndex, topInline, "right");
        this.setInlineNodeRangeBetween(topInline, 0, topInline, cutIndex, { inclusive });
      } else {
        const cutIndex = this.getIndexInParentInline(toNode, toIndex, topInline, "left");
        this.setInlineNodeRangeBetween(topInline, topInline.nodes.length, topInline, cutIndex, {
          inclusive,
        });
      }
    }

    const headPos = isForward ? toExprPos + 1 : toExprPos;
    tiptap.chain().setTextSelection({ from: anchorPos, to: headPos }).focus().run();
  }

  /**
   * Select a single node. It will reset selection and put tiptap selection around this node.
   */
  selectNode(node: NodeModel): InlineNodeRange | undefined {
    if (!node.parent?.is(InlineNodeModel)) {
      return;
    }
    this.resetSelection();
    return this.setInlineNodeRange(node.parent, node.index, node.index + 1);
  }

  isActive(node: NodeModel) {
    return this.activeNode === node;
  }

  get activeNode(): NodeModel | undefined {
    if (this.inlineNodeRanges.size === 1 && this.selectedNodes.length === 1) {
      return this.selectedNodes[0];
    }
  }

  /**
   * Returns the topmost selected node in the selection.
   */
  get topSelectedNode() {
    const { selectedNodes } = this;
    if (selectedNodes.length === 0) return null;

    return selectedNodes.reduce(
      (top, n) => {
        if (!top || top.ancestors.includes(n)) return n;
        return top;
      },
      null as NodeModel | null,
    );
  }

  /**
   * Increase selection in the inline node starting from the beginning (forward) or end (backward).
   * If there is no selection in this inline node, start the new one. If the existing selection starting
   * from the wrong side, start another selection.
   * Return undefined if selection rolls outside of the inline node boundaries.
   */
  rollSelection(
    inlineNode: InlineNodeModel,
    changeDirection: "right" | "left",
    selectionDirection: "forward" | "backward",
  ): { node: InlineNodeModel; anchor: number; head: number } | undefined {
    // Start selection from the correct side of the inline node
    let newAnchor = selectionDirection === "forward" ? 0 : inlineNode.nodes.length;
    let newHead = newAnchor;

    // If we already have the range in this inline node, use it instead
    const range = this.inlineNodeRanges.get(inlineNode.id);
    if (range) {
      newAnchor = range.anchor;
      newHead = range.head;
    }

    newHead += changeDirection === "right" ? 1 : -1;

    return this.setInlineNodeRange(inlineNode, newAnchor, newHead);
  }

  selectRight() {
    this.selectAfterCursor(() => {
      const { cursor } = this.doc;
      if (
        cursor.nodeAfterCursor &&
        this.startSelectionPosition?.node.ancestors.includes(cursor.nodeAfterCursor)
      ) {
        cursor.moveRight();
      } else {
        cursor.moveToNextSibling();
      }
    });
  }

  selectLeft() {
    this.selectAfterCursor(() => {
      const { cursor } = this.doc;
      if (
        cursor.nodeBeforeCursor &&
        this.startSelectionPosition?.node.ancestors.includes(cursor.nodeBeforeCursor)
      ) {
        cursor.moveLeft();
      } else {
        cursor.moveToPrevSibling();
      }
    });
  }

  selectUp() {
    this.selectAfterCursor(() => {
      const { cursor } = this.doc;
      cursor.moveUp();
    });
  }

  selectDown() {
    this.selectAfterCursor(() => {
      const { cursor } = this.doc;
      cursor.moveDown();
    });
  }

  /**
   * Build a cross-wrapper selection from an explicit anchor position to an
   * explicit head position. Used when two endpoints live in different wrappers.
   *
   * Returns `true` if the cross-wrapper selection was applied.
   */
  selectCrossExpression(
    fromNode: InlineNodeModel,
    fromIndex: number,
    toNode: InlineNodeModel,
    toIndex: number,
    options?: {
      /**
       * When true, the target range includes the node at `toIndex`
       */
      inclusiveTarget?: boolean;
    },
  ): boolean {
    const { tiptap } = this.doc.editor;
    if (!tiptap) return false;

    const fromWrapper = fromNode.ancestors.find((n): n is WrapperNodeModel =>
      n.is(WrapperNodeModel),
    );
    const toWrapper = toNode.ancestors.find((n): n is WrapperNodeModel => n.is(WrapperNodeModel));
    if (!fromWrapper || !toWrapper || fromWrapper === toWrapper) {
      return false;
    }

    let fromExprPos: number | undefined;
    let toExprPos: number | undefined;

    tiptap.state.doc.descendants((node, pos) => {
      const nodeId = node.attrs?.nodeId as string | undefined;

      if (typeof nodeId === "string") {
        if (nodeId === fromWrapper.id) fromExprPos = pos;
        if (nodeId === toWrapper.id) toExprPos = pos;
      }

      if (fromExprPos !== undefined && toExprPos !== undefined) {
        return false;
      }

      return true;
    });

    if (fromExprPos === undefined || toExprPos === undefined) {
      return false;
    }

    const isForward = toExprPos > fromExprPos;
    this.startSelectionPosition = { node: fromNode, index: fromIndex };

    this.doc.cursor.reset();
    this.doc.input.reset(true);

    this.extendCrossExpressionSourceRange(isForward ? "forward" : "backward");

    const newInline = toWrapper.inlineNode;
    const inclusive = options?.inclusiveTarget;

    if (newInline) {
      if (isForward) {
        const cutIndex = this.getIndexInParentInline(toNode, toIndex, newInline, "right");
        this.setInlineNodeRangeBetween(newInline, 0, newInline, cutIndex, { inclusive });
      } else {
        const cutIndex = this.getIndexInParentInline(toNode, toIndex, newInline, "left");
        this.setInlineNodeRangeBetween(newInline, newInline.nodes.length, newInline, cutIndex, {
          inclusive,
        });
      }
    }

    const anchorPos = isForward ? fromExprPos : fromExprPos + 1;
    const headPos = isForward ? toExprPos + 1 : toExprPos;
    tiptap.chain().setTextSelection({ from: anchorPos, to: headPos }).focus().run();

    return true;
  }

  /**
   * Extend the source expression's inline-node range from its anchor to
   * the edge in the direction of motion. When the anchor lives in a
   * nested inline (group, function parameter, etc.) the range is set on
   * the top-level inline of the enclosing wrapper, with the projection
   * including the entire nested group.
   */
  extendCrossExpressionSourceRange(
    direction: "forward" | "backward",
  ): WrapperNodeModel | undefined {
    const startPos = this.startSelectionPosition;
    if (!startPos) return undefined;

    const wrapper = startPos.node.ancestors.find((n) => n.is(WrapperNodeModel));
    if (!wrapper?.is(WrapperNodeModel)) return undefined;

    const topInline = wrapper.inlineNode;
    if (!topInline) return undefined;

    if (direction === "forward") {
      const cutIndex = this.getIndexInParentInline(
        startPos.node,
        startPos.index,
        topInline,
        "left",
      );
      this.setInlineNodeRangeBetween(topInline, cutIndex, topInline, topInline.nodes.length);
    } else {
      const cutIndex = this.getIndexInParentInline(
        startPos.node,
        startPos.index,
        topInline,
        "right",
      );
      this.setInlineNodeRangeBetween(topInline, 0, topInline, cutIndex);
    }

    return wrapper;
  }

  private getIndexInParentInline(
    node: InlineNodeModel,
    index: number,
    parentInline: InlineNodeModel,
    side: "left" | "right",
  ): number {
    if (node === parentInline) {
      return index;
    }

    if (node.parent?.is(WrapperNodeModel)) {
      return index;
    }

    let current: NodeModel = node;

    for (const ancestor of node.ancestors) {
      if (ancestor === parentInline) {
        const childIndex = current.index;
        return side === "right" ? childIndex + 1 : childIndex;
      }
      current = ancestor;
    }

    return side === "right" ? parentInline.nodes.length : 0;
  }

  setInlineNodeRangeBetween(
    fromNode: InlineNodeModel,
    fromIndex: number,
    toNode: InlineNodeModel,
    toIndex: number,
    options?: { inclusive?: boolean; isWrapperSelection?: boolean },
  ): { node: InlineNodeModel; anchor: number; head: number } | undefined {
    if (fromNode === toNode) {
      return this.setInlineNodeRange(fromNode, fromIndex, toIndex, options);
    }

    type Row = [InlineNodeModel, number];

    function mapAncestors(node: InlineNodeModel, idx: number) {
      let index = idx;
      return node.ancestors
        .reduce(
          (result, n) => {
            if (n.is(InlineNodeModel)) {
              result.push([n, index]);
            }
            index = n.index;
            return result;
          },
          [[node, index]] as Row[],
        )
        .reverse();
    }

    const fromAncestors = mapAncestors(fromNode, fromIndex);
    const toAncestors = mapAncestors(toNode, toIndex);

    if (!fromAncestors[0] || !toAncestors[0] || fromAncestors[0][0] !== toAncestors[0][0]) {
      return;
    }

    let lastCommonAncestor = 0;
    for (let i = 1; i < Math.max(fromAncestors.length, toAncestors.length); i += 1) {
      const fromRow = fromAncestors[i];
      const toRow = toAncestors[i];
      if (fromRow && toRow && fromRow[0] === toRow[0]) {
        lastCommonAncestor = i;
      } else {
        if (fromRow) this.inlineNodeRanges.delete(fromRow[0].id);
        if (toRow) this.inlineNodeRanges.delete(toRow[0].id);
      }
    }

    const from = fromAncestors[lastCommonAncestor];
    const to = toAncestors[lastCommonAncestor];

    let newFromIndex = from[1] === to[1] ? from[1] + 1 : from[1];
    const newToIndex = to[1];

    // If we started the selection inside the group and moved it outside, the group should be selected.
    if (from[0] !== fromNode && newFromIndex > newToIndex && from[1] !== to[1]) {
      newFromIndex += 1;
    }

    return this.setInlineNodeRange(from[0], newFromIndex, newToIndex, options);
  }

  /**
   * Set selection range for an inline node. It will check that anchor and head are within 0 and the
   * number of inline node children.
   * Returns undefined if range is invalid.
   */
  private setInlineNodeRange(
    node: InlineNodeModel,
    anchor: number,
    head: number,
    options?: {
      /** If inclusive is true, it will include the node next to head if it is to the right of anchor */
      inclusive?: boolean;
      isWrapperSelection?: boolean;
    },
  ): InlineNodeRange | undefined {
    const inclusive = !!options?.inclusive;
    const isWrapperSelection = !!options?.isWrapperSelection;

    const { id, nodes } = node;
    const from = Math.min(anchor, head);
    const to = Math.max(anchor, head);
    const value = { node, anchor, head, from, to, isWrapperSelection, key: id };

    if (inclusive && value.head > value.anchor) {
      value.head += 1;
    }

    if (anchor === head) {
      this.inlineNodeRanges.delete(id);
      return value;
    }
    if (anchor >= 0 && anchor <= nodes.length && head >= 0 && head <= nodes.length) {
      this.inlineNodeRanges.set(id, value);
      return value;
    }
    return undefined;
  }

  /**
   * Check if the wrapper node is fully selected.
   */
  isWrapperNodeSelected(
    wrapperNodeId: string,
    allowPartialSelection = false,
  ): InlineNodeRange | false {
    const wrapperNode = this.doc.nodes.get(wrapperNodeId);
    if (wrapperNode?.is(WrapperNodeModel)) {
      const { inlineNode } = wrapperNode;
      if (inlineNode) {
        const range = this.inlineNodeRanges.get(inlineNode.id);
        if (range) {
          if (allowPartialSelection) {
            return range;
          }
          const { from, to } = range;
          if (from === 0 && to === inlineNode.nodes.length) {
            return range;
          }
        }
      }
    }
    return false;
  }

  /**
   * Check that the node is within the inline node selection range. It will iterage ancestor to do this check
   * so it will work even if it is indirectly selelected via some ancestor's range.
   * Selectable nodes only those that are direct children of inline node.
   */
  private isInlineNodeRangeSelected(node: NodeModel): boolean {
    if (!node.parent?.is(InlineNodeModel)) {
      return false;
    }
    let checkIndex = node.index;
    const inlineNodeWithRange = node.ancestors.find((n) => {
      if (this.inlineNodeRanges.get(n.id)) {
        return true;
      }
      checkIndex = n.index;
      return false;
    });
    if (inlineNodeWithRange) {
      const range = this.inlineNodeRanges.get(inlineNodeWithRange.id);
      if (range) {
        const { anchor, head } = range;
        const [from, to] = anchor > head ? [head, anchor - 1] : [anchor, head - 1];
        return checkIndex >= from && checkIndex <= to;
      }
    }
    return false;
  }

  private resetInlineNodeRanges(): void {
    this.inlineNodeRanges = new Map();
  }

  resetWrapperNodes(): void {
    for (const [key, { node }] of this.inlineNodeRanges) {
      if (node.parent?.id && this.isWrapperNodeSelected(node.parent.id)) {
        this.inlineNodeRanges.delete(key);
      }
    }
  }

  selectWrapperNode(
    node: WrapperNodeModel,
    direction: "forward" | "backward" = "forward",
  ): InlineNodeRange | undefined {
    const { inlineNode } = node;
    if (
      inlineNode &&
      // !this.inlineNodeRanges.get(inlineNode.id) &&
      !this.doc.cursor.isOnNode(node)
    ) {
      if (direction === "forward") {
        return this.setInlineNodeRange(inlineNode, 0, inlineNode.nodes.length);
      }
      return this.setInlineNodeRange(inlineNode, inlineNode.nodes.length, 0);
    }
  }

  get allSelectedNodes(): NodeModel[] {
    return [...this.inlineNodeRanges.values()].reduce<NodeModel[]>((result, range) => {
      const { node } = range;
      return [...result, ...this.getSelectedNodes(node)];
    }, []);
  }
}
